import { Controller } from "@hotwired/stimulus"

// Hero SVG track map for the Admin::Tracks detail panel.
// Renders: real recorded track surface (a ribbon between the left/right outline
//          edges), sector ticks, dashed center line, corner markers at apex_m,
//          start/finish; plus — for the SELECTED corner — its start→end arc, the
//          race-direction chevrons, and the optimal racing line; plus a live
//          position scrub dot; with overlay toggles persisted to localStorage and
//          cursor-centred wheel-zoom that drives a shared distance window.
//
// Data values expected on the element:
//   data-track-map-hero-center-value           — JSON: [[x,y,z,wL,wR], …] downsampled ~250 pts
//   data-track-map-hero-racing-value           — JSON: [[x,y,z], …] downsampled ~250 pts
//   data-track-map-hero-edges-value            — JSON: {left:[[x,z]…], right:[[x,z]…]} (same stride as center)
//   data-track-map-hero-corners-value          — JSON: [{n, x, z, dir, name, source}, …] (legacy)
//   data-track-map-hero-corners-geometry-value — JSON: [{number, name, start_m, apex_m, end_m, direction, source}, …]
//   data-track-map-hero-sectors-value          — JSON: [i2, i3] or [] (legacy indices)
//   data-track-map-hero-sector-starts-value    — JSON: [m2, m3, …] sector boundaries (metres)
//   data-track-map-hero-sections-value         — JSON: [{id, number, name, start_m, end_m, source}, …]
//   data-track-map-hero-length-value           — number, meters (for badge)
//   data-track-map-hero-turn-count-value       — number (for badge)
//   data-track-map-hero-rotation-value         — number, degrees (presentation rotation)
//   data-track-map-hero-curated-value          — boolean (track has curated corners)
//
// Colors are sourced from CSS custom properties at connect() time for the themed
// tokens. The recorded-surface geometry colours (#1b1f33 / #474d77 / #3a3f63 /
// #6BE7FF / #A78BFA / #0c0e1a) are HUD-internal literals — the map is the fixed
// near-black broadcast HUD surface, identical in both page themes (see the
// design-system rule: this surface bypasses theming).

const STORAGE_KEY = "admin-tracks:overlays"
const DEFAULT_OVERLAYS = {
  racing: true,
  centerline: true,
  surface: true,
  corners: true,
  sections: true,
  sectors: true,
}

// Section band-arc palette — distinct hues at one chroma/lightness. MUST stay in
// sync with cw-sections.js PALETTE, corner_timeline_controller's SECTION_PALETTE,
// and TracksHelper::SECTION_BAND_PALETTE (Ruby). The swappable literals let a
// future green↔red time heat-map drop in without a relayout.
const SECTION_PALETTE = [
  "#5B8DEF",
  "#8B5BEF",
  "#C44FD0",
  "#E0598B",
  "#E08A4F",
  "#C9A227",
  "#5BBF73",
  "#3FB6B6",
  "#6E7BE0",
  "#B069D8",
  "#D86E9E",
  "#E0A14F",
  "#7FB84F",
  "#4FB89C",
]
const sectionColorFor = (i) =>
  SECTION_PALETTE[
    ((i % SECTION_PALETTE.length) + SECTION_PALETTE.length) %
      SECTION_PALETTE.length
  ]

// Timing-sector tick label colour (S1/S2/S3) — HUD-internal literal (fixed dark).
const SECTOR_TICK = "#e0e6ff"
const SECTOR_TICK_LABEL = "#aeb6dd"

const W = 1000
const H = 640
const PAD = 56

// Recorded-surface / overlay geometry literals (HUD-internal, fixed-dark).
const SURF_FILL = "#1b1f33"
const EDGE_STROKE = "#474d77"
const CENTER_DASH = "#3a3f63"
const RACE_LINE = "#6BE7FF"
const SEL_ARC = "#A78BFA"
const AUTO_CORE = "#14F4D9"
const HUD_NEAR = "#0c0e1a"

// Read tokens from `root` (the hero element, which carries `.dark` so the HUD —
// a permanent near-black surface — always resolves dark-theme color values,
// e.g. the lightened vivid-purple, regardless of the page theme).
function cssVar(name, root) {
  return getComputedStyle(root || document.documentElement)
    .getPropertyValue(name)
    .trim()
}

// Rotate a point's (x, z) plane by `deg` around the pivot (cx, cz). Returns the
// rotated [x, z]. Rotation is applied to the GEOMETRY (the points), not the SVG
// group — so corner number + name labels are drawn at rotated positions but kept
// upright (never transformed). This is the "labels readable while the track
// rotates" requirement, solved structurally.
// Angle is negated to match the session/telemetry map and the thumbnails (their
// screen y-axis points down — see makeProjector), so a given map_rotation_deg
// turns the track the same visual way everywhere.
function rotateXZ(x, z, deg, cx, cz) {
  if (!deg) return [x, z]
  const r = (-deg * Math.PI) / 180,
    cos = Math.cos(r),
    sin = Math.sin(r)
  const dx = x - cx,
    dz = z - cz
  return [cx + dx * cos - dz * sin, cz + dx * sin + dz * cos]
}

// Builds a projector: rotates all points (in the x/z plane, around the centroid)
// by `deg`, then fits them into w×h with padding. Screen y increases downward
// (z → screen-y, NOT flipped) to match the session/telemetry track map — the
// admin hero previously flipped Y, rendering every track vertically mirrored
// vs the (correct) session renderer.
//
// All point sets (center, edges, racing, the selected arc, the scrub dot) MUST
// be projected through ONE projector so the saved map_rotation_deg rotation +
// fit apply uniformly. Points are [x, y, z, …] (we read index 0 and 2); edge
// points (which are [x, z]) are wrapped as [x, 0, z] by callers.
function makeProjector(pointSets, w, h, pad, deg = 0) {
  // Centroid of all points (rotation pivot).
  let sx = 0,
    sz = 0,
    nn = 0
  for (const pts of pointSets) {
    for (const p of pts) {
      sx += p[0]
      sz += p[2]
      nn++
    }
  }
  const cx = nn ? sx / nn : 0
  const cz = nn ? sz / nn : 0

  // Fit bounds AFTER rotating.
  let minx = Infinity,
    minz = Infinity,
    maxx = -Infinity,
    maxz = -Infinity
  for (const pts of pointSets) {
    for (const p of pts) {
      const [rx, rz] = rotateXZ(p[0], p[2], deg, cx, cz)
      if (rx < minx) minx = rx
      if (rx > maxx) maxx = rx
      if (rz < minz) minz = rz
      if (rz > maxz) maxz = rz
    }
  }
  const gw = maxx - minx || 1
  const gh = maxz - minz || 1
  const s = Math.min((w - 2 * pad) / gw, (h - 2 * pad) / gh)
  const ox = (w - gw * s) / 2 - minx * s
  const oz = (h - gh * s) / 2 - minz * s
  const proj = (p) => {
    const [rx, rz] = rotateXZ(p[0], p[2], deg, cx, cz)
    return [rx * s + ox, rz * s + oz]
  }
  proj.cx = cx
  proj.cz = cz
  proj.s = s
  return proj
}

function toPathD(pts, proj, close = false) {
  let d = ""
  pts.forEach((p, i) => {
    const q = proj(p)
    d += (i === 0 ? "M" : "L") + q[0].toFixed(1) + " " + q[1].toFixed(1)
  })
  return d + (close ? "Z" : "")
}

// Wrap a 2-element edge point [x, z] as a [x, 0, z] point the projector reads.
function edgePt(e) {
  return [e[0], 0, e[1]]
}

// Perpendicular tick across the centerline at index idx.
// Returns {x1, y1, x2, y2} in SVG coords, half-length = half.
function perpendicularTick(center, proj, idx, half) {
  const n = center.length
  const c = proj(center[idx])
  const a = proj(center[(idx - 1 + n) % n])
  const b = proj(center[(idx + 1) % n])
  const dx = b[0] - a[0],
    dy = b[1] - a[1]
  const len = Math.hypot(dx, dy) || 1
  const nx = -dy / len,
    ny = dx / len
  return {
    x1: (c[0] + nx * half).toFixed(1),
    y1: (c[1] + ny * half).toFixed(1),
    x2: (c[0] - nx * half).toFixed(1),
    y2: (c[1] - ny * half).toFixed(1),
  }
}

function svgEl(tag, attrs = {}) {
  const el = document.createElementNS("http://www.w3.org/2000/svg", tag)
  for (const [k, v] of Object.entries(attrs)) el.setAttribute(k, v)
  return el
}

// ── Distance helpers over the downsampled centre-line ───────────────────────
// start_m / apex_m / end_m are meters along the full (1 m) centre-line. The
// downsampled centre preserves Euclidean distance, so cumulative arc-length over
// it ≈ real meters — close enough to locate arcs/markers/the scrub dot.

// Cumulative arc-length [s0=0, …] over the centre points (closed loop length too).
function buildArcLengths(center) {
  const n = center.length
  const s = new Array(n)
  s[0] = 0
  for (let i = 1; i < n; i++) {
    const a = center[i - 1],
      b = center[i]
    s[i] = s[i - 1] + Math.hypot(b[0] - a[0], b[2] - a[2])
  }
  const last = center[n - 1],
    first = center[0]
  const length = s[n - 1] + Math.hypot(first[0] - last[0], first[2] - last[2])
  return { s, length }
}

// Nearest centre index at or before distance m (binary search).
function mToIdx(s, m) {
  const n = s.length
  if (m <= 0) return 0
  if (m >= s[n - 1]) return n - 1
  let lo = 0,
    hi = n - 1
  while (hi - lo > 1) {
    const mid = (lo + hi) >> 1
    if (s[mid] < m) lo = mid
    else hi = mid
  }
  return lo
}

// Interpolated centre xyz at distance m → drives marker / scrub positions.
function ptAtM(center, s, m) {
  const n = center.length
  const mm = Math.max(0, Math.min(s[n - 1], m))
  const i = mToIdx(s, mm),
    j = Math.min(n - 1, i + 1)
  const span = s[j] - s[i] || 1,
    t = (mm - s[i]) / span
  const a = center[i],
    b = center[j]
  return [a[0] + (b[0] - a[0]) * t, 0, a[2] + (b[2] - a[2]) * t]
}

// Optimal racing line through a corner as world points (out–in–out) using the
// real recorded edges. Ported from cw-curvature.js `racingLine`. Apex hugs the
// inside edge; entry/exit swing to the outside. inside = left edge for a left
// corner, right edge for a right corner (matches TrackGeometry handedness).
function racingLine(center, s, edges, corner) {
  const Lf = edges.left,
    R = edges.right
  if (!Lf || !R || !Lf.length || !R.length) return null
  const i0 = mToIdx(s, corner.start_m),
    i1 = mToIdx(s, corner.end_m),
    ai = mToIdx(s, corner.apex_m)
  if (i1 - i0 < 2) return null
  const inside = corner.direction === "left" ? Lf : R
  const outside = corner.direction === "left" ? R : Lf
  const ss = (t) => {
    t = Math.max(0, Math.min(1, t))
    return t * t * (3 - 2 * t)
  }
  const K = 0.82,
    pts = []
  for (let i = i0; i <= i1; i++) {
    if (!inside[i] || !outside[i] || !center[i]) continue
    const lat =
      i <= ai
        ? -1 + 2 * ss((i - i0) / Math.max(1, ai - i0))
        : 1 - 2 * ss((i - ai) / Math.max(1, i1 - ai))
    const edge = lat >= 0 ? inside[i] : outside[i]
    const f = Math.abs(lat) * K
    const cx = center[i][0],
      cz = center[i][2]
    pts.push([cx + (edge[0] - cx) * f, 0, cz + (edge[1] - cz) * f])
  }
  return pts.length > 1 ? pts : null
}

export default class extends Controller {
  static values = {
    center: Array,
    racing: Array,
    edges: Object,
    corners: Array,
    cornersGeometry: Array,
    sectors: Array,
    sectorStarts: Array,
    sections: Array,
    length: Number,
    turnCount: Number,
    rotation: Number,
    curated: Boolean,
  }

  connect() {
    this._overlays = this._loadOverlays()
    this._rotation = this.rotationValue || 0 // current (live) rotation, deg
    this._hovered = null // corner number lit from hover
    this._selected = null // corner number selected/open
    this._hoverM = null // live scrub distance (meters) or null
    this._view = null // shared distance window {a, b} or null = full lap
    // ── Section tier (middle of the hierarchy) ───────────────────────────────
    // Stable working copy of the section tiling, seeded from the corner-curation
    // parent via setSections() (and on connect from the sections value so the
    // bands paint before the parent's first push). `??=` preserves a tiling the
    // parent pushed via setSections() BEFORE connect() ran (the connect-order
    // race) — an unconditional reset would wipe it.
    this._sections ??= Array.isArray(this.sectionsValue)
      ? this.sectionsValue.map((s) => ({ ...s }))
      : []
    this._selectedSectionId ??= null // selected section id (band lit) or null
    this._hoveredSectionId ??= null // section id glowing from hover or null
    // Cache arc-length over the centre once per connect (centre is static).
    const c = this.centerValue
    this._arc = c && c.length > 1 ? buildArcLengths(c) : { s: [], length: 0 }
    this._render()
  }

  // ── Public API (driven by the parent corner-curation controller, the timeline,
  //    and the orientation slice) ──────────────────────────────────────────────

  // Live preview rotation (no persistence). The orientation slice calls this on
  // drag/slider; we re-render at the new angle without writing map_rotation_deg.
  setRotation(deg) {
    this._rotation = Number(deg) || 0
    this._render()
  }

  // Light a corner's marker from outside (list-row hover → marker glow).
  highlight(number) {
    const n = Number(number)
    if (this._hovered === n) return
    this._hovered = n
    this._render()
  }

  clearHighlight() {
    if (this._hovered == null) return
    this._hovered = null
    this._render()
  }

  // Select a corner's marker from outside (map-driven selection is also
  // dispatched as a cornerSelect event the parent acts on). Renders the lit
  // start→end arc, chevrons, and racing line for the selected corner.
  select(number) {
    const n = number == null ? null : Number(number)
    this._selected = n
    this._render()
  }

  // Alias the plan/timeline use ("setSelected(number|null)").
  setSelected(number) {
    this.select(number)
  }

  // Corners ⇄ Sections mode, pushed from the corner-curation parent. The ribbon,
  // edges, centre line + sectors are shared across both modes. For Phase B: in
  // "sections" mode GHOST the corner markers (low opacity, non-interactive
  // context — the section band-arcs become the prominent layer in Phase E); in
  // "corners" mode they render at full strength.
  setMode(mode) {
    const next = mode === "sections" ? "sections" : "corners"
    if (this._mode === next) return
    this._mode = next
    this._render()
  }

  // Replace the section tiling from the corner-curation parent (Phase E). Stores
  // a stable copy (so a caller can't alias `_sections`), drops a stale selection
  // that no longer exists, and re-renders the band-arc layer. The parent calls
  // this on mode→sections, on a live boundary drag (preview), and on every
  // section-card reconnect (boundary/split/merge/rename/seed commit) so the
  // bands reflect the server's renumbered state. No-ops safely before connect().
  setSections(sectionsArray) {
    this._sections = Array.isArray(sectionsArray)
      ? sectionsArray.map((s) => ({ ...s }))
      : []
    if (
      this._selectedSectionId != null &&
      !this._sections.some(
        (s) => String(s.id) === String(this._selectedSectionId),
      )
    ) {
      this._selectedSectionId = null
    }
    if (
      this._hoveredSectionId != null &&
      !this._sections.some(
        (s) => String(s.id) === String(this._hoveredSectionId),
      )
    ) {
      this._hoveredSectionId = null
    }
    this._render()
  }

  // Light a section's band-arc from outside (list-row click, timeline band select,
  // map arc click routed back through the parent). null clears it. Does NOT
  // re-emit sectionSelect (reserved for user clicks on a map arc).
  setSelectedSection(id) {
    this._selectedSectionId = id == null ? null : String(id)
    if (this._mode === "sections" && this._sectionArcEls?.length)
      this._applySectionArcEmphasis()
    else this._render()
  }

  // Glow a section's band-arc from outside (list-row / timeline-band hover routed
  // through the parent). null clears it. Transient — must NOT touch selection.
  // Hovered emphasis sits between idle and selected (a light opacity/width bump),
  // mirroring the corner marker hover. Does NOT re-emit sectionHover (reserved for
  // user hover on a map arc).
  setHoveredSection(id) {
    const next = id == null ? null : String(id)
    if (this._hoveredSectionId === next) return
    this._hoveredSectionId = next
    if (this._mode === "sections" && this._sectionArcEls?.length)
      this._applySectionArcEmphasis()
    else this._render()
  }

  // Live position dot: white dot + mono "#### m" at the given distance, or clear
  // it when null. The timeline scrubber drives this.
  setHoverM(meters) {
    this._hoverM = meters == null ? null : Number(meters)
    this._render()
  }

  // Shared distance window {a, b} (meters). Zooms the map to that segment; full
  // lap when null / spanning the whole lap. The parent owns `view`; this method
  // lets it push the window in. Re-renders without re-emitting (avoids a loop).
  setView(view) {
    this._view =
      view && typeof view.a === "number" && typeof view.b === "number"
        ? view
        : null
    this._render()
  }

  // Replace the corners geometry from outside (live timeline handle drags +
  // server-renumbered commits) and re-render the markers / selected-corner
  // overlays. Preserves the current selection, hover, and view — this only
  // swaps the array the marker/arc layers read from.
  updateCorners(cornersArray) {
    if (!Array.isArray(cornersArray)) return
    this.cornersGeometryValue = cornersArray
    this._render()
  }

  // ── localStorage helpers ──────────────────────────────────────────────────

  _loadOverlays() {
    try {
      const stored = JSON.parse(localStorage.getItem(STORAGE_KEY) || "{}")
      return { ...DEFAULT_OVERLAYS, ...stored }
    } catch {
      return { ...DEFAULT_OVERLAYS }
    }
  }

  _saveOverlays() {
    try {
      localStorage.setItem(STORAGE_KEY, JSON.stringify(this._overlays))
    } catch {}
  }

  _toggleOverlay(key) {
    this._overlays[key] = !this._overlays[key]
    this._saveOverlays()
    this._render()
  }

  // Whether the current view is a real zoom (a strict sub-window of the lap).
  _zoomed() {
    const v = this._view,
      L = this._arc.length
    return !!(v && L > 0 && v.b - v.a < L * 0.985)
  }

  // Build the projector for the current render. Zoomed → frame only the visible
  // segment (+ margin) so the corner fills the map; otherwise frame the whole
  // centre. Everything projects through THIS projector (rotation included).
  _buildProjector() {
    const center = this.centerValue
    const racing = this.racingValue
    const edges = this.edgesValue || {}
    const { s, length } = this._arc

    if (this._zoomed()) {
      const v = this._view
      const mg = (v.b - v.a) * 0.18
      const i0 = mToIdx(s, Math.max(0, v.a - mg))
      const i1 = mToIdx(s, Math.min(length, v.b + mg))
      const seg = center.slice(i0, Math.max(i0 + 2, i1 + 1))
      return makeProjector([seg], W, H, PAD, this._rotation)
    }

    const pointSets = [center]
    if (racing && racing.length > 1) pointSets.push(racing)
    if (edges.left && edges.left.length) pointSets.push(edges.left.map(edgePt))
    if (edges.right && edges.right.length)
      pointSets.push(edges.right.map(edgePt))
    return makeProjector(pointSets, W, H, PAD, this._rotation)
  }

  // ── Main render ───────────────────────────────────────────────────────────

  _render() {
    // Defense in depth: `_arc` is built in connect(); a public method (e.g. the
    // parent's setSelected/setView) called before connect() runs would otherwise
    // reach `_buildProjector` → `this._arc` undefined and throw, blanking the map.
    // Bail until connected — the connect() render paints the real state.
    if (!this._arc) return

    // Clear previous render
    this.element.innerHTML = ""

    const center = this.centerValue
    const racing = this.racingValue
    const edges = this.edgesValue || {}
    if (!center || center.length < 2) return

    const proj = this._buildProjector()
    const { s } = this._arc
    const ov = this._overlays
    const hasEdges = !!(
      edges.left &&
      edges.left.length &&
      edges.right &&
      edges.right.length
    )

    // Read CSS color tokens once — themed tokens for markers/labels/chrome.
    const colorVividPurple = cssVar("--color-vivid-purple", this.element)
    const colorCyanTeal = cssVar("--color-cyan-teal", this.element)
    const colorTextFaint = cssVar("--app-text-faint", this.element)
    const colorTextMuted = cssVar("--app-text-muted", this.element)
    const colorHudBg = cssVar("--app-hud-bg", this.element)

    // Build outer SVG
    const svg = svgEl("svg", {
      viewBox: `0 0 ${W} ${H}`,
      width: "100%",
      height: "100%",
      preserveAspectRatio: "xMidYMid meet",
      style: "display:block;touch-action:none",
    })
    // Cursor-centred wheel-zoom → shared distance window.
    svg.addEventListener("wheel", (e) => this._onWheel(e, proj), {
      passive: false,
    })

    // ── Defs: glow filter ────────────────────────────────────────────────────
    const defs = svgEl("defs")
    const filter = svgEl("filter", {
      id: "hero-glow",
      x: "-20%",
      y: "-20%",
      width: "140%",
      height: "140%",
    })
    const blur = svgEl("feGaussianBlur", { stdDeviation: "3.4", result: "b" })
    const merge = svgEl("feMerge")
    merge.appendChild(svgEl("feMergeNode", { in: "b" }))
    merge.appendChild(svgEl("feMergeNode", { in: "SourceGraphic" }))
    filter.appendChild(blur)
    filter.appendChild(merge)
    defs.appendChild(filter)
    svg.appendChild(defs)

    const centerD = toPathD(center, proj, true)

    // ── Layer 1: Track surface ───────────────────────────────────────────────
    // Recorded ribbon between the real left/right outline edges (fill) + edge
    // strokes + a dashed centre-line. Falls back to the constant-width stroked
    // road when a track has no recorded edges (outline-only / legacy).
    if (ov.surface) {
      if (hasEdges) {
        const left = edges.left.map(edgePt)
        const right = edges.right.map(edgePt)
        // Ribbon: left edge forward, right edge reversed, closed.
        let ribbonD = toPathD(left, proj, false)
        for (let i = right.length - 1; i >= 0; i--) {
          const q = proj(right[i])
          ribbonD += " L" + q[0].toFixed(1) + " " + q[1].toFixed(1)
        }
        ribbonD += " Z"
        svg.appendChild(
          svgEl("path", { d: ribbonD, fill: SURF_FILL, stroke: "none" }),
        )
        svg.appendChild(
          svgEl("path", {
            d: toPathD(left, proj, true),
            fill: "none",
            stroke: EDGE_STROKE,
            "stroke-width": "1.5",
            "stroke-linejoin": "round",
            opacity: "0.9",
          }),
        )
        svg.appendChild(
          svgEl("path", {
            d: toPathD(right, proj, true),
            fill: "none",
            stroke: EDGE_STROKE,
            "stroke-width": "1.5",
            "stroke-linejoin": "round",
            opacity: "0.9",
          }),
        )
        // Dashed centre-line as part of the surface (always with the ribbon).
        svg.appendChild(
          svgEl("path", {
            d: centerD,
            fill: "none",
            stroke: CENTER_DASH,
            "stroke-width": "1",
            "stroke-dasharray": "2 7",
            opacity: "0.45",
          }),
        )
      } else {
        svg.appendChild(
          svgEl("path", {
            d: centerD,
            fill: "none",
            stroke: CENTER_DASH,
            "stroke-width": "18",
            "stroke-linejoin": "round",
            "stroke-linecap": "round",
          }),
        )
        svg.appendChild(
          svgEl("path", {
            d: centerD,
            fill: "none",
            stroke: SURF_FILL,
            "stroke-width": "14.5",
            "stroke-linejoin": "round",
            "stroke-linecap": "round",
          }),
        )
      }
    }

    // ── Layer 2a: Section band-arcs (the NEW middle tier of the hierarchy) ─────
    // Each section [start_m, end_m] is a translucent coloured arc stroked along
    // the centre-line through the SAME projector the ribbon uses. Prominent +
    // clickable in Sections mode (the foreground layer); faint context otherwise.
    if (ov.sections && this._sections && this._sections.length > 0) {
      this._renderSectionBands(svg, proj, center, s)
    }

    // ── Layer 2b: Timing-sector ticks (the coarse, game-provided tier) ─────────
    // A short perpendicular tick across the ribbon at each sector_starts distance
    // plus an S1/S2/S3 label. Distances are metres → located via the arc-length
    // table (matches the timeline + the design's profile.sectorStarts).
    if (ov.sectors) this._renderSectorTicks(svg, proj, center, s)

    // ── Layer 3: Center line (separate dashed overlay when toggled — only for
    //    the fallback road; with the ribbon the centre dash is part of surface) ─
    if (ov.centerline && !hasEdges) {
      svg.appendChild(
        svgEl("path", {
          d: centerD,
          fill: "none",
          stroke: colorTextFaint,
          "stroke-width": "1.3",
          "stroke-dasharray": "5 6",
          "stroke-linejoin": "round",
        }),
      )
    }

    // ── Layer 4: Racing line (whole-lap) ──────────────────────────────────────
    if (ov.racing && racing && racing.length > 1) {
      svg.appendChild(
        svgEl("path", {
          d: toPathD(racing, proj, true),
          fill: "none",
          stroke: colorVividPurple,
          "stroke-width": "3",
          "stroke-linejoin": "round",
          "stroke-linecap": "round",
          filter: "url(#hero-glow)",
        }),
      )
    }

    // ── Layer 5: Selected-corner overlays (arc + chevrons + racing line) ──────
    const sel = this._selectedCorner()
    if (sel) this._renderSelectedCorner(svg, sel, center, s, edges)

    // ── Layer 6: Corner markers (at apex_m, interpolated along centre-line) ───
    // In Sections mode the markers are ghosted context: a wrapping group drops
    // their opacity (~0.32) and disables interaction so the section layer reads
    // as the foreground (Phase E paints the band-arcs above it).
    if (ov.corners) {
      const ghost = this._mode === "sections"
      const markerHost = ghost
        ? svgEl("g", { opacity: "0.32", "pointer-events": "none" })
        : svg
      this._renderMarkers(markerHost, proj, center, s, {
        colorVividPurple,
        colorTextMuted,
        colorHudBg,
      })
      if (ghost) svg.appendChild(markerHost)
    }

    // ── Layer 7: Start / finish (always on with surface) ─────────────────────
    if (ov.surface && center.length > 1) {
      const startPt = proj(center[0])
      svg.appendChild(
        svgEl("circle", {
          cx: startPt[0].toFixed(1),
          cy: startPt[1].toFixed(1),
          r: "4.5",
          fill: "#fff",
          stroke: HUD_NEAR,
          "stroke-width": "1.5",
        }),
      )
    }

    // ── Layer 8: Live position dot (timeline scrub) ──────────────────────────
    if (this._hoverM != null) this._renderScrub(svg, proj, center, s)

    this.element.appendChild(svg)

    // ── Overlay toggle chips (top-left) ───────────────────────────────────────
    this.element.appendChild(
      this._buildChips(colorVividPurple, colorCyanTeal, colorTextFaint),
    )

    // ── Overview inset (top-right) — only when zoomed ────────────────────────
    if (this._zoomed())
      this.element.appendChild(this._buildOverviewInset(center, s))

    // ── Bottom overlay: length/corner badge (bottom-right) ───────────────────
    this.element.appendChild(this._buildBadge())
  }

  // ── Selected corner ───────────────────────────────────────────────────────

  _selectedCorner() {
    if (this._selected == null) return null
    const geo = this.cornersGeometryValue || []
    return geo.find((c) => Number(c.number) === Number(this._selected)) || null
  }

  _renderSelectedCorner(svg, sel, center, s, edges) {
    const proj = this._buildProjector()
    const i0 = mToIdx(s, sel.start_m),
      i1 = mToIdx(s, sel.end_m)

    // Lit start→end arc along the centre-line (purple, glow).
    if (i1 > i0) {
      const seg = []
      for (let i = i0; i <= i1; i++) seg.push(center[i])
      svg.appendChild(
        svgEl("path", {
          d: toPathD(seg, proj, false),
          fill: "none",
          stroke: SEL_ARC,
          "stroke-width": "9",
          "stroke-linecap": "round",
          opacity: "0.9",
          filter: "url(#hero-glow)",
        }),
      )
    }

    // Race-direction chevrons along the span (point the way you drive).
    const n = Math.max(2, Math.min(7, Math.round((i1 - i0) / 3)))
    for (let a = 1; a <= n; a++) {
      const i = Math.round(i0 + ((i1 - i0) * a) / (n + 1))
      const p = proj(center[i])
      const q = proj(center[Math.min(center.length - 1, i + 1)])
      const ang = (Math.atan2(q[1] - p[1], q[0] - p[0]) * 180) / Math.PI
      const g = svgEl("g", {
        transform: `translate(${p[0].toFixed(1)} ${p[1].toFixed(1)}) rotate(${ang.toFixed(1)})`,
        opacity: "0.5",
        "pointer-events": "none",
      })
      g.appendChild(
        svgEl("path", {
          d: "M -4 -5 L 4 0 L -4 5",
          fill: "none",
          stroke: "#fff",
          "stroke-width": "2",
          "stroke-linecap": "round",
          "stroke-linejoin": "round",
        }),
      )
      svg.appendChild(g)
    }

    // Optimal racing line through the corner (cyan, dashed, out-in-out).
    const pts = racingLine(center, s, edges, sel)
    if (pts) {
      svg.appendChild(
        svgEl("path", {
          d: toPathD(pts, proj, false),
          fill: "none",
          stroke: RACE_LINE,
          "stroke-width": "2.4",
          "stroke-linecap": "round",
          "stroke-dasharray": "1 7",
          opacity: "0.95",
        }),
      )
    }
  }

  // ── Section band-arcs (middle tier) ─────────────────────────────────────────
  //
  // For each section, stroke the centre-line arc between its two boundary
  // distances with its palette colour. In Sections mode the arcs are the
  // foreground (full opacity, wider stroke, clickable → emit sectionSelect;
  // the selected one glows brightest); in Corners mode they're faint context.
  // Mirrors design/cw-map.jsx `sectionArcs`. Honours the `sections` layer flag
  // (gated by the caller).
  _renderSectionBands(svg, proj, center, s) {
    const sections = this._sections
    const isSec = this._mode === "sections"
    // Keep refs to the interactive arc paths so hover/select can update emphasis
    // in place (see _applySectionArcEmphasis) instead of rebuilding the SVG —
    // rebuilding on hover destroyed the arc the pointer was over, so real clicks
    // never landed.
    this._sectionArcEls = []

    sections.forEach((sec, i) => {
      const start = Number(sec.start_m)
      const end = Number(sec.end_m)
      if (!(end > start)) return
      const i0 = mToIdx(s, start),
        i1 = mToIdx(s, end)
      if (i1 <= i0) return

      const seg = []
      for (let k = i0; k <= i1; k++) seg.push(center[k])
      if (seg.length < 2) return

      const id = sec.id == null ? null : String(sec.id)
      const selected = id != null && this._selectedSectionId === id
      const hovered = !selected && id != null && this._hoveredSectionId === id
      const color = sectionColorFor(i)
      // Emphasis ordering in Sections mode: idle (0.42) < hover (0.66) < selected
      // (0.95); the stroke widens a touch on hover too. Corners mode is flat context.
      const opacity = isSec ? (selected ? 0.95 : hovered ? 0.66 : 0.42) : 0.26
      const width = isSec ? (selected ? "12" : hovered ? "10" : "8") : "5"

      const attrs = {
        d: toPathD(seg, proj, false),
        fill: "none",
        stroke: color,
        "stroke-width": width,
        "stroke-linecap": "round",
        opacity: String(opacity),
      }
      if (selected) attrs.filter = "url(#hero-glow)"

      const path = svgEl("path", attrs)
      // Clickable + hoverable only in Sections mode (the bands own selection/hover
      // there). In Corners mode they're non-interactive context so corner hit-areas
      // win.
      if (isSec && id != null) {
        path.style.cursor = "pointer"
        path.style.pointerEvents = "stroke"
        // Hover/select update arc emphasis IN PLACE (no _render()): rebuilding the
        // SVG on hover destroyed this very path between mousedown and mouseup, so
        // real clicks never landed. State is read fresh from the controller.
        path.addEventListener("click", (e) => {
          e.stopPropagation()
          this._selectedSectionId = id
          this._applySectionArcEmphasis()
          this.dispatch("sectionSelect", { detail: { id }, bubbles: true })
        })
        path.addEventListener("mouseenter", () => {
          this._hoveredSectionId = id
          this._applySectionArcEmphasis()
          this.dispatch("sectionHover", { detail: { id }, bubbles: true })
        })
        path.addEventListener("mouseleave", () => {
          this._hoveredSectionId = null
          this._applySectionArcEmphasis()
          this.dispatch("sectionLeave", { detail: { id }, bubbles: true })
        })
        this._sectionArcEls.push({ id, i, color, path })
      } else {
        path.style.pointerEvents = "none"
      }
      svg.appendChild(path)
    })
  }

  // Recompute each section arc's emphasis (opacity · stroke width · selected glow)
  // from the current selected/hovered state, mutating the existing <path> nodes IN
  // PLACE — no _render(). Used by hover and select so the arc survives the pointer
  // interaction; a full SVG rebuild would swap it out mid-click. Sections mode only.
  _applySectionArcEmphasis() {
    const els = this._sectionArcEls
    if (!els || !els.length) return
    for (const { id, path } of els) {
      const selected = id != null && this._selectedSectionId === id
      const hovered = !selected && id != null && this._hoveredSectionId === id
      path.setAttribute(
        "opacity",
        String(selected ? 0.95 : hovered ? 0.66 : 0.42),
      )
      path.setAttribute("stroke-width", selected ? "12" : hovered ? "10" : "8")
      if (selected) path.setAttribute("filter", "url(#hero-glow)")
      else path.removeAttribute("filter")
    }
  }

  // ── Timing-sector ticks (coarse tier) ───────────────────────────────────────
  //
  // Perpendicular tick across the ribbon + S1/S2/S3 label at each sector_starts
  // distance (metres). Located via the arc-length table so it matches the
  // timeline's metre-based sector axis. Falls back to the legacy index-based
  // sectorsValue when sector_starts isn't supplied. Mirrors cw-map.jsx sectorTicks.
  _renderSectorTicks(svg, proj, center, s) {
    const starts = Array.isArray(this.sectorStartsValue)
      ? this.sectorStartsValue
      : []
    if (starts.length > 0) {
      starts.forEach((m, i) => {
        const dist = Number(m)
        if (!Number.isFinite(dist)) return
        const idx = mToIdx(s, dist)
        if (idx < 0 || idx >= center.length) return
        this._renderSectorTick(svg, proj, center, idx, `S${i + 1}`)
      })
      return
    }
    // Legacy fallback: indices into the centre-line.
    const idxs = Array.isArray(this.sectorsValue) ? this.sectorsValue : []
    idxs.forEach((idx, i) => {
      if (idx < 0 || idx >= center.length) return
      this._renderSectorTick(svg, proj, center, idx, `S${i + 1}`)
    })
  }

  _renderSectorTick(svg, proj, center, idx, label) {
    const t = perpendicularTick(center, proj, idx, 13)
    svg.appendChild(
      svgEl("line", {
        x1: t.x1,
        y1: t.y1,
        x2: t.x2,
        y2: t.y2,
        stroke: SECTOR_TICK,
        "stroke-width": "2.5",
        "stroke-linecap": "round",
        opacity: "0.7",
      }),
    )
    // Label nudged off the outer end of the tick (away from the ribbon).
    const c = proj(center[idx])
    const ox = Number(t.x1) - c[0],
      oy = Number(t.y1) - c[1]
    const len = Math.hypot(ox, oy) || 1
    const lx = (c[0] + (ox / len) * 22).toFixed(1)
    const ly = (c[1] + (oy / len) * 22).toFixed(1)
    const txt = svgEl("text", {
      x: lx,
      y: ly,
      dy: "3",
      "text-anchor": "middle",
      "font-size": "9.5",
      "font-family": "'JetBrains Mono', monospace",
      "font-weight": "700",
      fill: SECTOR_TICK_LABEL,
    })
    txt.textContent = label
    svg.appendChild(txt)
  }

  // ── Corner markers ────────────────────────────────────────────────────────

  _renderMarkers(svg, proj, center, s, colors) {
    const geo = this.cornersGeometryValue
    // Prefer the apex_m geometry (markers move live as apex drags); fall back to
    // the legacy pre-projected corners value when geometry is unavailable.
    if (geo && geo.length > 0) {
      for (const c of geo) {
        const xyz = ptAtM(center, s, c.apex_m)
        const p = proj(xyz)
        if (this._zoomed() && !this._inFrame(p)) continue
        this._renderMarker(
          svg,
          p,
          {
            number: c.number,
            name: c.name,
            source: c.source,
          },
          colors,
        )
      }
      return
    }

    const corners = this.cornersValue
    if (!corners || corners.length === 0) return
    for (const c of corners) {
      const p = proj([c.x, 0, c.z])
      if (this._zoomed() && !this._inFrame(p)) continue
      this._renderMarker(
        svg,
        p,
        { number: c.n, name: c.name, source: c.source },
        colors,
      )
    }
  }

  _renderMarker(svg, p, c, colors) {
    const { colorVividPurple, colorTextMuted, colorHudBg } = colors
    const curated = !!this.curatedValue
    const isManual = c.source === "manual"
    // Provenance colour: curated track → manual purple / still-auto muted;
    // uncurated track → cyan (today's look).
    const core = curated
      ? isManual
        ? colorVividPurple
        : colorTextMuted
      : AUTO_CORE
    const active = this._hovered === c.number || this._selected === c.number
    const r = active ? 13 : 10
    const named = c.name && String(c.name).trim()
    const showLabel = !!named || active

    const g = svgEl("g")
    g.style.cursor = "pointer"
    g.dataset.cornerNumber = c.number

    if (active) {
      g.appendChild(
        svgEl("circle", {
          cx: p[0].toFixed(1),
          cy: p[1].toFixed(1),
          r: (r + 6).toFixed(1),
          fill: core,
          opacity: "0.18",
        }),
      )
    }

    g.appendChild(
      svgEl("circle", {
        cx: p[0].toFixed(1),
        cy: p[1].toFixed(1),
        r: r.toFixed(1),
        fill: colorHudBg || HUD_NEAR,
        stroke: core,
        "stroke-width": active ? "2.2" : "1.5",
      }),
    )
    const txt = svgEl("text", {
      x: p[0].toFixed(1),
      y: p[1].toFixed(1),
      dy: "3.6",
      "text-anchor": "middle",
      "font-size": "11",
      "font-family": "'JetBrains Mono', monospace",
      "font-weight": "600",
      fill: core,
    })
    txt.textContent = c.number
    g.appendChild(txt)

    if (showLabel) {
      const dx = p[0] - W / 2,
        dy = p[1] - H / 2
      const L = Math.hypot(dx, dy) || 1
      let lx = p[0] + (dx / L) * 22,
        ly = p[1] + (dy / L) * 22
      const labelText = named ? c.name : `Turn ${c.number}`
      const anchor = lx < p[0] - 4 ? "end" : lx > p[0] + 4 ? "start" : "middle"
      const approxW = labelText.length * 12.5 * 0.58
      const M = 6
      const minX =
        anchor === "end"
          ? lx - approxW
          : anchor === "start"
            ? lx
            : lx - approxW / 2
      const maxX =
        anchor === "end"
          ? lx
          : anchor === "start"
            ? lx + approxW
            : lx + approxW / 2
      if (minX < M) lx += M - minX
      else if (maxX > W - M) lx -= maxX - (W - M)
      ly = Math.max(18, Math.min(H - 30, ly))
      const haloC = colorHudBg || HUD_NEAR
      const halo = svgEl("text", {
        x: lx.toFixed(1),
        y: ly.toFixed(1),
        dy: "3.5",
        "text-anchor": anchor,
        "font-size": "12.5",
        "font-family": "'Space Grotesk', sans-serif",
        "font-weight": "600",
        fill: haloC,
        stroke: haloC,
        "stroke-width": "3.5",
        "stroke-linejoin": "round",
        opacity: "0.85",
      })
      halo.textContent = labelText
      g.appendChild(halo)
      const fill = svgEl("text", {
        x: lx.toFixed(1),
        y: ly.toFixed(1),
        dy: "3.5",
        "text-anchor": anchor,
        "font-size": "12.5",
        "font-family": "'Space Grotesk', sans-serif",
        "font-weight": "600",
        fill: named ? "rgba(240,240,245,0.95)" : "rgba(255,255,255,0.55)",
      })
      fill.textContent = labelText
      g.appendChild(fill)
    }

    g.addEventListener("mouseenter", () => {
      this._hovered = c.number
      this._render()
      this.dispatch("cornerHover", {
        detail: { number: c.number },
        bubbles: true,
      })
    })
    g.addEventListener("mouseleave", () => {
      this._hovered = null
      this._render()
      this.dispatch("cornerLeave", {
        detail: { number: c.number },
        bubbles: true,
      })
    })
    g.addEventListener("click", () => {
      this._selected = c.number
      this._render()
      this.dispatch("cornerSelect", {
        detail: { number: c.number },
        bubbles: true,
      })
    })

    svg.appendChild(g)
  }

  // ── Live position scrub dot ───────────────────────────────────────────────

  _renderScrub(svg, proj, center, s) {
    const xyz = ptAtM(center, s, this._hoverM)
    const sp = proj(xyz)
    const m = Math.round(this._hoverM)
    const g = svgEl("g", { "pointer-events": "none" })
    g.appendChild(
      svgEl("circle", {
        cx: sp[0].toFixed(1),
        cy: sp[1].toFixed(1),
        r: "10",
        fill: "#fff",
        opacity: "0.12",
      }),
    )
    g.appendChild(
      svgEl("circle", {
        cx: sp[0].toFixed(1),
        cy: sp[1].toFixed(1),
        r: "4.5",
        fill: "#fff",
        stroke: HUD_NEAR,
        "stroke-width": "1.5",
      }),
    )
    const ly = (sp[1] - 13).toFixed(1)
    const halo = svgEl("text", {
      x: sp[0].toFixed(1),
      y: ly,
      "text-anchor": "middle",
      "font-size": "11",
      "font-family": "'JetBrains Mono', monospace",
      "font-weight": "700",
      fill: HUD_NEAR,
      stroke: HUD_NEAR,
      "stroke-width": "3.5",
      "stroke-linejoin": "round",
      opacity: "0.85",
    })
    halo.textContent = `${m} m`
    g.appendChild(halo)
    const fill = svgEl("text", {
      x: sp[0].toFixed(1),
      y: ly,
      "text-anchor": "middle",
      "font-size": "11",
      "font-family": "'JetBrains Mono', monospace",
      "font-weight": "700",
      fill: "#fff",
    })
    fill.textContent = `${m} m`
    g.appendChild(fill)
    svg.appendChild(g)
  }

  // ── Wheel-zoom → shared distance window ───────────────────────────────────

  _onWheel(e, proj) {
    const L = this._arc.length
    const center = this.centerValue
    if (!L || !center || center.length < 2) return
    e.preventDefault()

    const rect = e.currentTarget.getBoundingClientRect()
    const sc = Math.min(rect.width / W, rect.height / H)
    const vx = (e.clientX - rect.left - (rect.width - W * sc) / 2) / sc
    const vy = (e.clientY - rect.top - (rect.height - H * sc) / 2) / sc

    // Nearest centre index to the cursor (in the current projection).
    let best = 0,
      bd = Infinity
    for (let i = 0; i < center.length; i++) {
      const q = proj(center[i])
      const d = (q[0] - vx) ** 2 + (q[1] - vy) ** 2
      if (d < bd) {
        bd = d
        best = i
      }
    }
    const mc = this._arc.s[best]
    const view = this._view || { a: 0, b: L }
    const span = view.b - view.a
    const minSpan = Math.min(140, L)
    const ns = Math.max(
      minSpan,
      Math.min(L, span * (e.deltaY > 0 ? 1.18 : 1 / 1.18)),
    )
    let a = mc - (mc - view.a) * (ns / span)
    if (a < 0) a = 0
    if (a + ns > L) a = L - ns
    if (a < 0) a = 0
    const next = ns >= L * 0.999 ? null : { a, b: a + ns }
    this.setView(next)
    // Sync the timeline: the parent owns `view` and re-pushes to both surfaces.
    this.dispatch("viewChange", { detail: { view: next }, bubbles: true })
  }

  _inFrame(p) {
    return p[0] > -40 && p[0] < W + 40 && p[1] > -40 && p[1] < H + 40
  }

  // ── Overview inset (top-right) ────────────────────────────────────────────

  _buildOverviewInset(center, s) {
    const w = 132,
      h = 78,
      pad = 12
    const proj = makeProjector([center], w, h, pad, this._rotation)
    const full = toPathD(center, proj, true)
    const v = this._view
    const i0 = mToIdx(s, v.a),
      i1 = mToIdx(s, v.b)
    const seg = center.slice(i0, Math.max(i0 + 2, i1 + 1))
    const segD = toPathD(seg, proj, false)

    const wrap = document.createElement("div")
    wrap.className =
      "absolute top-3 right-3 z-10 rounded-lg bg-glossy-black-blue/70 ring-1 ring-white/10 backdrop-blur px-2 pt-1.5 pb-1 pointer-events-none"

    const svg = svgEl("svg", {
      width: w,
      height: h,
      viewBox: `0 0 ${w} ${h}`,
      style: "display:block",
    })
    svg.appendChild(
      svgEl("path", {
        d: full,
        fill: "none",
        stroke: "#5b6388",
        "stroke-width": "2",
        "stroke-linejoin": "round",
        "stroke-linecap": "round",
        opacity: "0.55",
      }),
    )
    svg.appendChild(
      svgEl("path", {
        d: segD,
        fill: "none",
        stroke: SEL_ARC,
        "stroke-width": "3",
        "stroke-linejoin": "round",
        "stroke-linecap": "round",
      }),
    )
    wrap.appendChild(svg)

    const readout = document.createElement("div")
    readout.className =
      "text-center font-mono text-[8.5px] text-white/55 tabular-nums -mt-0.5"
    readout.textContent = `${(v.a / 1000).toFixed(2)}–${(v.b / 1000).toFixed(2)} km`
    wrap.appendChild(readout)

    return wrap
  }

  // ── Overlay chip row ──────────────────────────────────────────────────────

  _buildChips(purple, teal, faint) {
    const LAYERS = [
      { key: "racing", label: "Racing line", dot: purple },
      { key: "centerline", label: "Center line", dot: faint },
      { key: "surface", label: "Track surface", dot: "rgba(255,255,255,0.6)" },
      { key: "corners", label: "Corners", dot: teal },
      { key: "sections", label: "Sections", dot: SECTION_PALETTE[0] },
      { key: "sectors", label: "Sectors", dot: "rgba(255,255,255,0.4)" },
    ]

    const wrap = document.createElement("div")
    wrap.className = "absolute top-3 left-3 z-10 flex flex-wrap gap-1.5"

    for (const { key, label, dot } of LAYERS) {
      const btn = document.createElement("button")
      const active = !!this._overlays[key]
      btn.type = "button"
      btn.className = [
        "flex items-center gap-1.5 rounded-full px-2.5 py-1 text-[11px] font-display font-medium leading-none",
        "transition-opacity",
        active
          ? "bg-glossy-black-blue/60 text-white ring-1 ring-white/10"
          : "bg-glossy-black-blue/30 text-white/40 ring-1 ring-white/5",
      ].join(" ")
      btn.setAttribute("aria-pressed", String(active))
      btn.setAttribute("title", (active ? "Hide " : "Show ") + label)

      const dotEl = document.createElement("span")
      dotEl.style.cssText = `width:6px;height:6px;border-radius:50%;background:${dot};flex-shrink:0`
      btn.appendChild(dotEl)

      const labelEl = document.createElement("span")
      labelEl.textContent = label
      btn.appendChild(labelEl)

      btn.addEventListener("click", () => this._toggleOverlay(key))
      wrap.appendChild(btn)
    }

    return wrap
  }

  // ── Length / corner badge (bottom-right) ─────────────────────────────────

  _buildBadge() {
    const wrap = document.createElement("div")
    wrap.className =
      "absolute bottom-3 right-4 z-10 pointer-events-none text-right"

    const lengthM = this.lengthValue
    const turns = this.turnCountValue

    if (lengthM > 0) {
      const km = (lengthM / 1000).toFixed(3)
      const line1 = document.createElement("div")
      line1.className =
        "font-display text-2xl font-bold text-white leading-none"
      line1.innerHTML = `${km}<span style="font-size:0.875rem;font-weight:400;color:rgba(255,255,255,0.45);margin-left:3px">km</span>`
      wrap.appendChild(line1)
    }

    if (turns > 0) {
      const line2 = document.createElement("div")
      line2.className = "font-mono text-[11px] mt-1"
      line2.style.color = "rgba(255,255,255,0.4)"
      line2.textContent = `${turns} corners`
      wrap.appendChild(line2)
    }

    return wrap
  }
}
